<template>
    <transition name="igm-fade">
        <div class="igm-overlay" v-if="visible" @click.self="close" role="dialog" aria-modal="true">
            <div class="igm-modal">
                <!-- Header -->
                <div class="igm-header">
                    <h5 class="igm-title">{{ title || 'Gallery' }}</h5>
                    <button class="igm-close" @click="close" aria-label="Close">&times;</button>
                </div>

                <!-- Grid -->
                <div class="igm-body">
                    <div v-if="images && images.length" class="igm-grid">
                        <div
                            class="igm-cell"
                            v-for="(img, idx) in images"
                            :key="img.id || idx"
                            @click="openLightbox(idx)"
                        >
                            <img
                                :src="img[imageKey] || img"
                                :alt="img.alt || (title + ' image ' + (idx + 1))"
                                class="igm-thumb"
                            />
                        </div>
                    </div>
                    <p v-else class="igm-empty">No images available.</p>
                </div>
            </div>

            <!-- Lightbox -->
            <transition name="igm-fade">
                <div class="igm-lightbox" v-if="lightboxIndex !== null" @click.self="closeLightbox">
                    <button class="igm-lb-close" @click="closeLightbox" aria-label="Close">&times;</button>
                    <button class="igm-lb-arrow igm-lb-prev" @click.stop="lbPrev" v-if="images.length > 1">&#8249;</button>
                    <img
                        :src="images[lightboxIndex][imageKey] || images[lightboxIndex]"
                        :alt="'Image ' + (lightboxIndex + 1)"
                        class="igm-lb-img"
                    />
                    <button class="igm-lb-arrow igm-lb-next" @click.stop="lbNext" v-if="images.length > 1">&#8250;</button>
                    <p class="igm-lb-counter">{{ lightboxIndex + 1 }} / {{ images.length }}</p>
                </div>
            </transition>
        </div>
    </transition>
</template>

<script>
export default {
    name: "ImageGalleryModal",
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        images: {
            type: Array,
            default: () => []
        },
        imageKey: {
            type: String,
            default: "image_path"
        },
        title: {
            type: String,
            default: "Gallery"
        }
    },
    data() {
        return {
            lightboxIndex: null
        };
    },
    watch: {
        visible(val) {
            document.body.style.overflow = val ? "hidden" : "";
            if (!val) this.lightboxIndex = null;
        }
    },
    mounted() {
        window.addEventListener("keydown", this.onKey);
    },
    beforeDestroy() {
        window.removeEventListener("keydown", this.onKey);
        document.body.style.overflow = "";
    },
    methods: {
        close() {
            this.$emit("close");
        },
        openLightbox(idx) {
            this.lightboxIndex = idx;
        },
        closeLightbox() {
            this.lightboxIndex = null;
        },
        lbPrev() {
            this.lightboxIndex = (this.lightboxIndex - 1 + this.images.length) % this.images.length;
        },
        lbNext() {
            this.lightboxIndex = (this.lightboxIndex + 1) % this.images.length;
        },
        onKey(e) {
            if (!this.visible) return;
            if (e.key === "Escape") {
                if (this.lightboxIndex !== null) this.closeLightbox();
                else this.close();
            }
            if (this.lightboxIndex !== null) {
                if (e.key === "ArrowLeft") this.lbPrev();
                if (e.key === "ArrowRight") this.lbNext();
            }
        }
    }
};
</script>

<style scoped>
/* ── Overlay ────────────────────────────────── */
.igm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

/* ── Modal box ──────────────────────────────── */
.igm-modal {
    background: #fff;
    border-radius: 8px;
    width: 100%;
    max-width: 900px;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.22);
}

.igm-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 22px;
    border-bottom: 1px solid #e8e8e8;
    flex-shrink: 0;
}

.igm-title {
    font-size: 17px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
}

.igm-close {
    background: none;
    border: none;
    font-size: 26px;
    line-height: 1;
    color: #666;
    cursor: pointer;
    padding: 0 4px;
}
.igm-close:hover { color: #1a1a1a; }

/* ── Grid ───────────────────────────────────── */
.igm-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.igm-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.igm-cell {
    aspect-ratio: 1 / 1;
    overflow: hidden;
    border-radius: 6px;
    cursor: pointer;
    background: #f0f0f0;
    border: 1px solid #e4e4e4;
    transition: transform 0.15s, box-shadow 0.15s;
}
.igm-cell:hover { transform: scale(1.03); box-shadow: 0 4px 14px rgba(0,0,0,0.14); }

.igm-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.igm-empty { color: #888; text-align: center; padding: 30px 0; }

/* ── Lightbox ───────────────────────────────── */
.igm-lightbox {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.88);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
}

.igm-lb-img {
    max-width: 88vw;
    max-height: 82vh;
    object-fit: contain;
    border-radius: 4px;
    display: block;
}

.igm-lb-close {
    position: fixed;
    top: 18px;
    right: 24px;
    background: none;
    border: none;
    color: #fff;
    font-size: 38px;
    line-height: 1;
    cursor: pointer;
    z-index: 10001;
}

.igm-lb-arrow {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255,255,255,0.15);
    border: none;
    color: #fff;
    font-size: 46px;
    line-height: 1;
    padding: 2px 14px;
    cursor: pointer;
    border-radius: 4px;
    z-index: 10001;
    transition: background 0.15s;
}
.igm-lb-arrow:hover { background: rgba(255,255,255,0.28); }
.igm-lb-prev { left: 16px; }
.igm-lb-next { right: 16px; }

.igm-lb-counter {
    position: fixed;
    bottom: 18px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255,255,255,0.75);
    font-size: 14px;
    margin: 0;
}

/* ── Transition ─────────────────────────────── */
.igm-fade-enter-active, .igm-fade-leave-active { transition: opacity 0.2s; }
.igm-fade-enter, .igm-fade-leave-to { opacity: 0; }

/* ── Responsive ─────────────────────────────── */
@media (max-width: 767px) {
    .igm-grid { grid-template-columns: repeat(2, 1fr); }
    .igm-lb-arrow { font-size: 32px; padding: 2px 10px; }
}
</style>
